import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { CURRENT_SEASON } from '@/lib/season';

interface UserPrediction {
  id: string;
  user_id: string;
  fixture_id: string;
  predicted_outcome: 'home' | 'draw' | 'away';
  selected_team: string;
  used_double_down: boolean;
  matchday: number;
  season: string;
  points_earned: number;
  created_at: string;
  updated_at: string;
}

export const useUserPredictions = (userId?: string, season: string = CURRENT_SEASON, matchday?: number, leagueId?: string) => {
  return useQuery({
    queryKey: ['user-predictions', userId, season, matchday, leagueId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await invokeEdgeFunction('get-user-predictions', {
        body: { userId, season, matchday, leagueId },
      });

      if (error) {
        console.error('Error fetching user predictions:', error);
        throw new Error(error.message);
      }

      return (data ?? []) as UserPrediction[];
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useMatchdayPredictions = (season: string = CURRENT_SEASON, matchday: number) => {
  return useQuery({
    queryKey: ['matchday-predictions', season, matchday],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_predictions')
        .select('*')
        .eq('season', season)
        .eq('matchday', matchday)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching matchday predictions:', error);
        throw error;
      }

      return data as UserPrediction[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};